import type {
  CreateSessionRequest,
  ImageUploadResponse,
  OperationResult,
  ResultImageInfo,
  SourceImageRef,
} from '../../../domain';

/**
 * Pure helpers for the Image Lab session lifecycle + image lifting. The
 * `useImageLabSession` hook composes these with React state and the
 * `useCreateSession` mutation; testing the rules here keeps the React layer
 * thin and the rules portable.
 */

const SESSION_NAME_FALLBACK = 'Image Lab session';

const titleCase = (slug: string): string =>
  slug
    .split(/[-_\s]+/)
    .filter(Boolean)
    .map(part => part.charAt(0).toUpperCase() + part.slice(1))
    .join(' ');

/**
 * Build a friendly default session name from a studio id. `'generic'` →
 * `'Image Lab — Generic'`; an unknown / empty studio falls back to the
 * generic label. Kept simple — users can rename via the session API later.
 */
export function buildSessionCreateRequest(studioId: string | undefined): CreateSessionRequest {
  const slug = (studioId ?? '').trim();
  if (!slug) return { name: SESSION_NAME_FALLBACK };
  return {
    name: `Image Lab — ${titleCase(slug)}`,
    description: `Auto-created on first operation for studio ${slug}`,
  };
}

/**
 * Build a SourceImageRef for the canvas's active image. Prefers the
 * server-side `id` when the result image carries one (gallery / persisted
 * asset) so the backend can resolve via `generated_assets` lookup; falls
 * back to `url` when the id is missing (transient/data-URL paths).
 *
 * **Contract (audited 2026-05-25, `qa/i2i-payload-audit-2026-05-25.md` §2.1):**
 * Every code path that feeds `images` in `ImageLab.tsx` produces an
 * `id`-carrying `ResultImageInfo`: op results (`appendResultImages`),
 * uploads (`uploadResponseToResultImage`), gallery handoffs
 * (`assetSummaryToResultImage`), and filter-bake uploads. The `url`
 * fallback exists only for defensive reasons — in practice, the `id`
 * branch always fires for canvas-active sources. User-pasted reference
 * URLs (via `ReferenceImageInput`) go directly to `sourceImageUrl` and
 * intentionally bypass this helper.
 */
export function sourceFromImage(image: ResultImageInfo | undefined): SourceImageRef | undefined {
  if (!image) return undefined;
  if (image.id) return { sourceImageId: image.id };
  if (image.url) return { sourceImageUrl: image.url };
  return undefined;
}

/**
 * Lift the images produced by an OperationResult into the canvas's image
 * list. Appends to the existing list (so previous outputs stay accessible
 * via the carousel) and returns the new list plus the index of the first
 * newly-added image — the caller advances `activeIndex` to that so the
 * canvas auto-shows the latest output, matching v1's "jump to newest" UX.
 *
 * Returns the input list unchanged when the result has no images.
 */
export function appendResultImages(
  existing: ResultImageInfo[],
  result: OperationResult,
): { images: ResultImageInfo[]; firstNewIndex: number } {
  const nextImages = result.results?.images ?? [];
  if (nextImages.length === 0) {
    return { images: existing, firstNewIndex: Math.max(0, existing.length - 1) };
  }
  return {
    images: [...existing, ...nextImages],
    firstNewIndex: existing.length,
  };
}

/**
 * Adapt an `ImageUploadResponse` (from `AssetService.uploadImage` /
 * `uploadImageBase64`) into the `ResultImageInfo` shape the canvas's
 * `images` state holds. Renames `imageId` -> `id` and drops the
 * upload-specific fields (`sessionId`, `uploadedAt`).
 *
 * The result has the same shape as a Generate output, so all the existing
 * source-image-loop machinery (`sourceFromImage`, `appendResultImages` for
 * synthetic ops) treats it identically.
 */
export function uploadResponseToResultImage(resp: ImageUploadResponse): ResultImageInfo {
  return {
    id: resp.imageId,
    url: resp.url,
    thumbnailUrl: resp.thumbnailUrl,
    // 2026-05-26 — thread signedUrl through so downstream consumers (Konva
    // canvas, bake re-fetch, cross-origin Image loads) hit the HMAC-signed
    // endpoint instead of falling back to the unsigned `url` (which 401s on
    // crossOrigin='anonymous'). Closes
    // qa/bugs/2026-05-26-invert-filter-produces-black-image.md and
    // qa/bugs/2026-05-26-asset-file-401-recurrence-likely-ttl.md.
    signedUrl: resp.signedUrl,
    signedThumbnailUrl: resp.signedThumbnailUrl,
    width: resp.width,
    height: resp.height,
    format: resp.format,
    sizeBytes: resp.sizeBytes,
  };
}

/**
 * Append a single uploaded image to the canvas's image list. Unlike
 * `appendResultImages` (which takes an OperationResult shape), this takes
 * the adapted `ResultImageInfo` directly. The caller advances `activeIndex`
 * to the returned `firstNewIndex` so the freshly-uploaded image becomes
 * the active source for the next op.
 */
export function appendUploadedImage(
  existing: ResultImageInfo[],
  image: ResultImageInfo,
): { images: ResultImageInfo[]; firstNewIndex: number } {
  return {
    images: [...existing, image],
    firstNewIndex: existing.length,
  };
}
